/**
 * Relationship Items Page
 *
 * Reusable page for each relationship type / UI family under
 * /prioritization/relationships/*. Uses EntityListPage (Items list template)
 * with directionality columns — zero empty state or many-row table.
 *
 * Mirror folds (UI only; stored edge types preserved):
 *   sequence    ← dependency + prerequisite
 *   composition ← aggregation + breakdown
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowRight,
  GitBranch,
  Info,
  Trash2,
  X,
} from 'lucide-react';
import { EntityListPage } from '../../components/collection';
import type { SortOption, TableColumnWithRender } from '../../components/collection/types';
import { AgentBar } from '../../components/agents/AgentBar';
import { inversionAgent } from '../../components/agents/contextualAgents';
import { CreateCanonicalClusterModal } from '../../components/prioritization/item-view/CreateCanonicalClusterModal';
import { CreatePackageModal } from '../../components/prioritization/item-view/CreatePackageModal';
import { CreateExclusiveModal } from '../../components/prioritization/item-view/CreateExclusiveModal';
import { AddSequenceLinkModal } from '../../components/prioritization/item-view/AddSequenceLinkModal';
import { AddCompositionModal } from '../../components/prioritization/item-view/AddCompositionModal';
import { AddElaborationModal } from '../../components/prioritization/item-view/AddElaborationModal';
import { AddContainsModal } from '../../components/prioritization/item-view/AddContainsModal';
import { ResolveClusterModal } from '../../components/prioritization/relationships/ResolveClusterModal';
import { isUnresolvedCluster } from '../../lib/clusterResolution';
import { ReverseActionButton } from '../../components/reversals/ReverseActionButton';
import { reopenResolvedCluster } from '../../lib/reversals/reversalsService';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { useItemRelationshipsStore } from '../../stores/prioritization/itemRelationshipsStore';
import { useTenantStore } from '../../stores/prioritization/tenantStore';
import { getSafeSupabaseClient } from '../../lib/supabaseSync';
import { useUserStore } from '../../stores/prioritization/userStore';
import { normalizeSequencePair } from '../../lib/sequenceRelationships';
import {
  getRelationshipGuideEntry,
  relationshipPillClass,
} from '../../lib/relationshipGuide';
import { CATEGORY_BADGE, CATEGORY_BADGE_FALLBACK, CATEGORY_DOT, CATEGORY_DOT_FALLBACK } from '../../lib/itemCategoryColors';
import { edgeMaterializesIntoPlanning } from '../../lib/itemToPlanningAdapter';
import type { Candidate, ItemFrame, ItemLanguageType } from '../../types/prioritization/lifecycle';
import type {
  ItemRelationshipEdge,
  ItemRelationshipGroup,
  RelationshipEdgeType,
  RelationshipGroupType,
} from '../../types/prioritization/itemRelationships';
import type { LineageEvent } from '../../types/prioritization/itemLineage';
import {
  DiagramAggregation,
  DiagramCluster,
  DiagramContains,
  DiagramDependency,
  DiagramElaboration,
  DiagramExclusive,
  DiagramLineage,
  DiagramPackage,
  DiagramReframe,
} from '../../components/prioritization/RelationshipDiagrams';

// ============================================================================
// Types
// ============================================================================

export type RelationshipPageType =
  | 'cluster'
  | 'package'
  | 'exclusive'
  | 'dependency'
  | 'prerequisite'
  | 'sequence'
  | 'aggregation'
  | 'breakdown'
  | 'composition'
  | 'contains'
  | 'elaboration'
  | 'reframe'
  | 'lineage';

interface RelationshipItemsPageProps {
  relationshipType: RelationshipPageType;
}

type ListRowKind = 'group' | 'edge' | 'reframe' | 'lineage';

type LineageEventConfig = { label: string; verb: string; bg: string; text: string };

interface RelationshipListRow {
  id: string;
  kind: ListRowKind;
  searchText: string;
  primaryLabel: string;
  secondaryLabel: string;
  directionLabel: string;
  roleLabel: string;
  familyLabel: string;
  statusLabel: string;
  rationale: string;
  href?: string;
  group?: ItemRelationshipGroup;
  groupItems?: Candidate[];
  edge?: ItemRelationshipEdge;
  sourceItem?: Candidate;
  targetItem?: Candidate;
  reframe?: ReframeGroup;
  lineage?: LineageLogEntry;
  /** Resolved at row-build time so detail render never re-indexes event_type. */
  lineageEventConfig?: LineageEventConfig;
}

interface GroupWithItems {
  group: ItemRelationshipGroup;
  items: Candidate[];
}

interface EdgeWithItems {
  edge: ItemRelationshipEdge;
  sourceItem: Candidate | undefined;
  targetItem: Candidate | undefined;
}

interface ReframeGroup {
  item: Candidate;
  canonicalFrame: ItemFrame | undefined;
  secondaryFrames: ItemFrame[];
}

interface LineageLogEntry {
  event: LineageEvent;
  sourceItems: (Candidate | undefined)[];
  targetItems: (Candidate | undefined)[];
}

// ============================================================================
// Page config — icons/colors from relationshipGuide
// ============================================================================

const ELABORATION_GUIDE = getRelationshipGuideEntry('elaboration');

const PAGE_META: Record<
  RelationshipPageType,
  {
    guideId: string;
    title: string;
    subtitle: string;
    emptyMessage: string;
    createLabel: string;
    definition: string;
    diagram: React.ReactNode;
    itemLabel: string;
  }
> = {
  cluster: {
    guideId: 'cluster',
    title: 'Clusters',
    subtitle: 'Items grouped by identity — duplicates, near-duplicates, and variants.',
    emptyMessage: 'No clusters have been created yet.',
    createLabel: 'New Cluster',
    definition:
      'Use a Cluster when two or more items describe the same underlying need or intent — just worded differently or submitted independently.',
    diagram: <DiagramCluster />,
    itemLabel: 'cluster',
  },
  package: {
    guideId: 'package',
    title: 'Packages',
    subtitle: 'Atomic groups — items that must be prioritized together or selected together.',
    emptyMessage: 'No packages have been created yet.',
    createLabel: 'New Package',
    definition:
      'A Package is an atomic group: the items inside it are prioritized as a unit and/or must be selected together.',
    diagram: <DiagramPackage />,
    itemLabel: 'package',
  },
  exclusive: {
    guideId: 'exclusive',
    title: 'Competing',
    subtitle: 'Mutually exclusive sets — one or the other, not both.',
    emptyMessage: 'No competing sets have been created yet.',
    createLabel: 'New Competing Set',
    definition:
      'A Competing set marks items that are mutually exclusive: at most one member may receive a commitment-quality disposition.',
    diagram: <DiagramExclusive />,
    itemLabel: 'competing set',
  },
  dependency: {
    guideId: 'sequence',
    title: 'Sequence & conditions',
    subtitle: 'What must be addressed before an item, and which items are waiting on it.',
    emptyMessage: 'No sequence or condition relationships have been declared yet.',
    createLabel: 'New Sequence Link',
    definition:
      'Dependency and Prerequisite are mirror assertions of the same predecessor→dependent structure.',
    diagram: <DiagramDependency />,
    itemLabel: 'sequence link',
  },
  prerequisite: {
    guideId: 'sequence',
    title: 'Sequence & conditions',
    subtitle: 'What must be addressed before an item, and which items are waiting on it.',
    emptyMessage: 'No sequence or condition relationships have been declared yet.',
    createLabel: 'New Sequence Link',
    definition:
      'Dependency and Prerequisite are mirror assertions of the same predecessor→dependent structure.',
    diagram: <DiagramDependency />,
    itemLabel: 'sequence link',
  },
  sequence: {
    guideId: 'sequence',
    title: 'Sequence & conditions',
    subtitle: 'What must be addressed before an item, and which items are waiting on it.',
    emptyMessage: 'No sequence or condition relationships have been declared yet.',
    createLabel: 'New Sequence Link',
    definition:
      'Dependency and Prerequisite are mirror assertions of the same predecessor→dependent structure. This table normalizes both stored edge types into one sequence family while preserving authored form for audit.',
    diagram: <DiagramDependency />,
    itemLabel: 'sequence link',
  },
  aggregation: {
    guideId: 'composition',
    title: 'Composition',
    subtitle: 'Whole-and-part structure, regardless of authorship viewpoint.',
    emptyMessage: 'No composition relationships exist yet.',
    createLabel: 'New Composition',
    definition:
      'Composition combines Aggregation and Breakdown because both store the same parent→child whole-part structure.',
    diagram: <DiagramAggregation />,
    itemLabel: 'composition',
  },
  breakdown: {
    guideId: 'composition',
    title: 'Composition',
    subtitle: 'Whole-and-part structure, regardless of authorship viewpoint.',
    emptyMessage: 'No composition relationships exist yet.',
    createLabel: 'New Composition',
    definition:
      'Composition combines Aggregation and Breakdown because both store the same parent→child whole-part structure.',
    diagram: <DiagramAggregation />,
    itemLabel: 'composition',
  },
  composition: {
    guideId: 'composition',
    title: 'Composition',
    subtitle: 'Whole-and-part structure, regardless of authorship viewpoint.',
    emptyMessage: 'No composition relationships exist yet.',
    createLabel: 'New Composition',
    definition:
      'Composition combines Aggregation and Breakdown in the UI because both store the same parent→child whole-part structure. Authored edge type remains available for audit.',
    diagram: <DiagramAggregation />,
    itemLabel: 'composition',
  },
  contains: {
    guideId: 'contains',
    title: 'Contains',
    subtitle: 'Passive parent→child hierarchy for reporting and navigation.',
    emptyMessage: 'No Contains relationships exist yet.',
    createLabel: 'New Contains',
    definition:
      'Contains is a passive parent→child link for navigation and reporting. It does not assert prioritization composition.',
    diagram: <DiagramContains />,
    itemLabel: 'contains link',
  },
  elaboration: {
    guideId: 'elaboration',
    title: 'Elaborations',
    subtitle: ELABORATION_GUIDE?.tagline ?? 'A continuum of possibilities, from minimally sufficient to ideal.',
    emptyMessage: 'No elaboration relationships exist yet.',
    createLabel: 'New Elaboration',
    definition: ELABORATION_GUIDE?.description
      ?? 'Elaboration maps the possibilities for fulfilling a parent need along a continuum of sufficiency.',
    diagram: <DiagramElaboration />,
    itemLabel: 'elaboration',
  },
  reframe: {
    guideId: 'reframe',
    title: 'Additional Categories',
    subtitle: 'Items with more than one category — the same item described more than one way.',
    emptyMessage: 'No items have an additional category yet.',
    createLabel: 'Add Category',
    definition:
      'An additional category lets a single item be described as more than one category at once.',
    diagram: <DiagramReframe />,
    itemLabel: 'item',
  },
  lineage: {
    guideId: 'lineage',
    title: 'Lineage',
    subtitle: 'Structural changes — split, fork, merge, supersede, or retire.',
    emptyMessage: 'No lineage events have been recorded yet.',
    createLabel: 'New Lineage Event',
    definition:
      'Lineage tracks provenance over time — splits, merges, promotions, and demotions.',
    diagram: <DiagramLineage />,
    itemLabel: 'event',
  },
};

function guideIcon(guideId: string) {
  const entry = getRelationshipGuideEntry(guideId);
  if (!entry) return <GitBranch className="h-4 w-4 text-[var(--text-ter)]" />;
  const Icon = entry.Icon;
  return <Icon className={`h-4 w-4 ${entry.iconColor}`} />;
}

function guideIconBg(guideId: string): string {
  return getRelationshipGuideEntry(guideId)?.color.split(' ')[0] ?? 'bg-[var(--slate-dim)]';
}

function directionForPage(pageType: RelationshipPageType, edge: ItemRelationshipEdge): string {
  if (pageType === 'sequence' || pageType === 'dependency' || pageType === 'prerequisite') {
    return 'Before → Waiting on';
  }
  if (pageType === 'composition' || pageType === 'aggregation' || pageType === 'breakdown') {
    return 'Whole → Part';
  }
  if (pageType === 'contains') return 'Parent → Child';
  if (pageType === 'elaboration') return 'Need → Possibility';
  return edge.label || edge.subtype || edge.type;
}

function roleForPage(pageType: RelationshipPageType, edge: ItemRelationshipEdge): string {
  if (pageType === 'sequence' || pageType === 'dependency' || pageType === 'prerequisite') {
    return edge.type === 'prerequisite' ? 'Asserted as prerequisite' : 'Asserted as dependency';
  }
  if (pageType === 'composition' || pageType === 'aggregation' || pageType === 'breakdown') {
    return edge.type === 'breakdown' ? 'Authored as breakdown' : 'Authored as aggregation';
  }
  if (edge.subtype) return String(edge.subtype);
  return '—';
}

// ============================================================================
// Data hooks
// ============================================================================

function useGroupedData(type: RelationshipPageType): GroupWithItems[] {
  const groups = useItemRelationshipsStore((s) => s.groups);
  const groupMemberships = useItemRelationshipsStore((s) => s.groupMemberships);

  // Group pages drop unresolved members entirely (`.filter(i => i != null)`
  // below) rather than showing a raw id, so the same lookup gap that turns edge
  // tables into uuids quietly shrinks a Cluster or Package here — and a Package
  // that renders 2 of its 3 members misrepresents an atomic group.
  const referencedIds = useMemo(
    () => new Set(groupMemberships.map((m) => m.itemId)),
    [groupMemberships],
  );
  const itemMapResolved = useResolvedItems(referencedIds);

  return useMemo(() => {
    if (type !== 'cluster' && type !== 'package' && type !== 'exclusive') return [];
    const groupType: RelationshipGroupType = type;
    const matchingGroups = groups.filter((g) => g.type === groupType && g.status !== 'dissolved');
    const itemMap = itemMapResolved;
    return matchingGroups.map((group) => {
      const memberItemIds = groupMemberships.filter((m) => m.groupId === group.id).map((m) => m.itemId);
      const items = memberItemIds.map((id) => itemMap.get(id)).filter((i): i is Candidate => i != null);
      return { group, items };
    });
  }, [type, groups, groupMemberships, itemMapResolved]);
}

/**
 * Fills in items an edge references that the items store does not supply.
 *
 * Every relationship page renders `sourceItem`/`targetItem` with the raw id as
 * a fallback, so a lookup miss silently degrades the whole table to unreadable
 * UUIDs — observed on production as 156 of 156 endpoints on
 * `/prioritization/relationships/sequence`, and the same on composition.
 *
 * The data is not at fault: all 78 Backspace sequence edges point at live
 * Backspace items, the store logs "Loaded 316", and reaching the page from the
 * Items page renders every title correctly. Only a cold load of the
 * relationship page itself misses, and it never recovers — a re-render does not
 * repair it. Rather than leave the table dependent on that ambient timing, this
 * resolves the gap directly, mirroring what `useLineageLog` in this same file
 * has always done for lineage events.
 */
function useResolvedItems(referencedIds: Set<string>): Map<string, Candidate> {
  const storeItems = useItemsStore((s) => s.items);
  const [resolved, setResolved] = useState<Map<string, Candidate>>(new Map());

  const storeMap = useMemo(() => new Map(storeItems.map((i) => [i.id, i])), [storeItems]);

  useEffect(() => {
    const missing = [...referencedIds].filter((id) => !storeMap.has(id) && !resolved.has(id));
    if (missing.length === 0) return;
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    let cancelled = false;
    void supabase
      .from('items')
      .select('id, title, description, item_category')
      .in('id', missing)
      .then(({ data, error }) => {
        if (cancelled || error || !data?.length) return;
        setResolved((prev) => {
          const next = new Map(prev);
          for (const row of data) {
            next.set(row.id, {
              id: row.id,
              title: row.title ?? '(untitled)',
              description: row.description ?? '',
              itemCategory: row.item_category ?? undefined,
            } as unknown as Candidate);
          }
          return next;
        });
      });
    return () => { cancelled = true; };
  }, [referencedIds, storeMap, resolved]);

  return useMemo(() => new Map([...storeMap, ...resolved]), [storeMap, resolved]);
}

function useEdgeData(type: RelationshipPageType): EdgeWithItems[] {
  const edges = useItemRelationshipsStore((s) => s.edges);

  // Endpoints this page will render, so a miss can be repaired rather than
  // shown as a bare uuid.
  const referencedIds = useMemo(() => {
    const ids = new Set<string>();
    for (const e of edges) {
      if (e.status === 'dissolved') continue;
      ids.add(e.sourceItemId);
      ids.add(e.targetItemId);
    }
    return ids;
  }, [edges]);

  const allItemsMap = useResolvedItems(referencedIds);
  const allItems = useMemo(() => [...allItemsMap.values()], [allItemsMap]);

  return useMemo(() => {
    const edgePageTypes: RelationshipPageType[] = [
      'dependency',
      'prerequisite',
      'sequence',
      'aggregation',
      'elaboration',
      'breakdown',
      'composition',
      'contains',
    ];
    if (!edgePageTypes.includes(type)) return [];

    const itemMap = new Map(allItems.map((i) => [i.id, i]));
    let matchingEdges: ItemRelationshipEdge[];

    if (type === 'sequence' || type === 'dependency' || type === 'prerequisite') {
      const byPair = new Map<string, ItemRelationshipEdge>();
      for (const edge of edges) {
        const pair = normalizeSequencePair(edge);
        if (!pair) continue;
        const key = `${pair.predecessorItemId}:${pair.dependentItemId}`;
        if (!byPair.has(key)) {
          byPair.set(key, {
            ...edge,
            sourceItemId: pair.predecessorItemId,
            targetItemId: pair.dependentItemId,
          });
        }
      }
      matchingEdges = [...byPair.values()];
    } else if (type === 'composition' || type === 'aggregation' || type === 'breakdown') {
      const byPair = new Map<string, ItemRelationshipEdge>();
      for (const edge of edges) {
        if ((edge.type !== 'aggregation' && edge.type !== 'breakdown') || edge.status === 'dissolved') continue;
        const key = `${edge.sourceItemId}:${edge.targetItemId}`;
        if (!byPair.has(key)) byPair.set(key, edge);
      }
      matchingEdges = [...byPair.values()];
    } else {
      const edgeType = type as RelationshipEdgeType;
      matchingEdges = edges.filter((edge) => edge.type === edgeType && edge.status !== 'dissolved');
    }

    return matchingEdges.map((edge) => ({
      edge,
      sourceItem: itemMap.get(edge.sourceItemId),
      targetItem: itemMap.get(edge.targetItemId),
    }));
  }, [type, edges, allItems]);
}

function useReframeGroups(): ReframeGroup[] {
  const allItems = useItemsStore((s) => s.items);
  return useMemo(
    () =>
      allItems
        .filter((item) => item.frames && item.frames.length > 0)
        .map((item) => {
          const frames = item.frames ?? [];
          const canonicalFrame =
            frames.find((f) => f.isCanonical) ??
            (item.canonicalFrameId ? frames.find((f) => f.id === item.canonicalFrameId) : undefined) ??
            frames.find((f) => f.isOriginal);
          const secondaryFrames = frames.filter((f) => f !== canonicalFrame);
          return { item, canonicalFrame, secondaryFrames };
        }),
    [allItems],
  );
}

function useLineageLog(): { entries: LineageLogEntry[]; loading: boolean } {
  const lineageEvents = useItemRelationshipsStore((s) => s.lineageEvents);
  const storeItems = useItemsStore((s) => s.items);
  const storeItemsMap = useMemo(() => new Map(storeItems.map((i) => [i.id, i])), [storeItems]);
  const [resolvedMap, setResolvedMap] = useState<Map<string, Candidate>>(new Map());
  const [loading, setLoading] = useState(false);

  const allReferencedIds = useMemo(() => {
    const ids = new Set<string>();
    for (const ev of lineageEvents) {
      ev.sourceItemIds.forEach((id) => ids.add(id));
      ev.targetItemIds.forEach((id) => ids.add(id));
    }
    return ids;
  }, [lineageEvents]);

  useEffect(() => {
    if (allReferencedIds.size === 0) return;
    const missing = [...allReferencedIds].filter((id) => !storeItemsMap.has(id) && !resolvedMap.has(id));
    if (missing.length === 0) return;
    setLoading(true);
    const supabase = getSafeSupabaseClient();
    if (!supabase) {
      setLoading(false);
      return;
    }
    void supabase
      .from('items')
      .select('id, title, description, item_category')
      .in('id', missing)
      .then(({ data, error }) => {
        if (!error && data && data.length > 0) {
          setResolvedMap((prev) => {
            const next = new Map(prev);
            for (const row of data) {
              next.set(row.id, {
                id: row.id,
                title: row.title ?? '(untitled)',
                description: row.description ?? '',
                itemCategory: row.item_category ?? undefined,
              } as unknown as Candidate);
            }
            return next;
          });
        }
        setLoading(false);
      });
  }, [allReferencedIds, storeItemsMap, resolvedMap]);

  const entries = useMemo(() => {
    const merged = new Map<string, Candidate>([...storeItemsMap, ...resolvedMap]);
    return [...lineageEvents]
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
      .map((event) => ({
        event,
        sourceItems: event.sourceItemIds.map((id) => merged.get(id)),
        targetItems: event.targetItemIds.map((id) => merged.get(id)),
      }));
  }, [lineageEvents, storeItemsMap, resolvedMap]);

  return { entries, loading };
}

// ============================================================================
// About modal
// ============================================================================

function AboutModal({
  title,
  definition,
  diagram,
  guideId,
  onClose,
}: {
  title: string;
  definition: string;
  diagram: React.ReactNode;
  guideId: string;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-[var(--bg-raised)] shadow-2xl border border-[var(--rule)]/80 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--rule)]">
          <div className="flex items-center gap-2.5">
            <div className={`w-7 h-7  flex items-center justify-center ${guideIconBg(guideId)}`}>
              {guideIcon(guideId)}
            </div>
            <span className="text-base font-semibold text-[var(--text-pri)]">{title}</span>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)]">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-6 py-5">
          <p className="text-sm text-[var(--text-ter)] leading-relaxed mb-5">{definition}</p>
          <div className="overflow-hidden">{diagram}</div>
        </div>
      </div>
    </div>
  );
}

function ItemTitleLink({ item, fallbackId }: { item?: Candidate; fallbackId?: string }) {
  if (!item) {
    return <span className="text-sm text-[var(--text-ter)] italic">{fallbackId ? `${fallbackId.slice(0, 8)}…` : 'Unknown item'}</span>;
  }
  return (
    <Link
      to={`/prioritization/items/${item.id}`}
      className="text-sm font-medium text-[var(--text-pri)] hover:text-[var(--text-ter)] truncate block min-w-0"
      onClick={(e) => e.stopPropagation()}
    >
      {item.title || 'Untitled'}
    </Link>
  );
}

function FrameTypeTag({ type }: { type: ItemLanguageType }) {
  const badge = CATEGORY_BADGE[type] ?? CATEGORY_BADGE_FALLBACK;
  const dot = CATEGORY_DOT[type] ?? CATEGORY_DOT_FALLBACK;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text} capitalize`}>
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
      {type}
    </span>
  );
}

const LINEAGE_EVENT_CONFIG_FALLBACK: LineageEventConfig = {
  label: 'Lineage',
  verb: 'changed',
  bg: 'bg-[var(--surface)]',
  text: 'text-[var(--text-sec)]',
};

/** Canonical types plus observed free-text DB values that are not in LineageEventType. */
const LINEAGE_EVENT_CONFIG: Record<string, LineageEventConfig> = {
  split: { label: 'Split', verb: 'was split into', bg: 'bg-[var(--green-dim)]', text: 'text-[var(--green)] dark:text-[var(--text-ter)]' },
  merge: { label: 'Merge', verb: 'merged into', bg: 'bg-[var(--slate-dim)]', text: 'text-[var(--text-sec)]' },
  fork: { label: 'Fork', verb: 'was forked into', bg: 'bg-[var(--slate-dim)]', text: 'text-[var(--text-sec)]' },
  supersede: { label: 'Supersede', verb: 'was superseded by', bg: 'bg-[var(--mustard-dim)]', text: 'text-[var(--mustard)]' },
  retire: { label: 'Retire', verb: 'was retired', bg: 'bg-[var(--surface)]', text: 'text-[var(--text-sec)]' },
  // Seed / legacy free-text event_type values seen in workspaces
  aggregate: { label: 'Aggregate', verb: 'aggregated into', bg: 'bg-[var(--surface)]', text: 'text-[var(--text-sec)]' },
  breakdown: { label: 'Breakdown', verb: 'broke down into', bg: 'bg-[var(--slate-dim)]', text: 'text-[var(--text-sec)]' },
  link: { label: 'Link', verb: 'linked to', bg: 'bg-cyan-50', text: 'text-cyan-700' },
  reframe: { label: 'Additional Category', verb: 'was also categorized as', bg: 'bg-[var(--coral-dim)]', text: 'text-[var(--coral)]' },
};

/** DB `event_type` is free text — never index without a guaranteed config object. */
function getLineageEventConfig(eventType: string | undefined | null): LineageEventConfig {
  const key = (eventType ?? '').trim().toLowerCase();
  if (key) {
    const known = LINEAGE_EVENT_CONFIG[key];
    if (known) return known;
    return {
      label: eventType!.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
      verb: 'changed',
      bg: LINEAGE_EVENT_CONFIG_FALLBACK.bg,
      text: LINEAGE_EVENT_CONFIG_FALLBACK.text,
    };
  }
  return LINEAGE_EVENT_CONFIG_FALLBACK;
}

// ============================================================================
// Main page
// ============================================================================

export default function RelationshipItemsPage({ relationshipType }: RelationshipItemsPageProps) {
  const meta = PAGE_META[relationshipType];
  const guideId = meta.guideId;
  const { focusGroupId } = useParams<{ focusGroupId?: string }>();
  const navigate = useNavigate();
  const can = useUserStore((s) => s.can);
  // can() reads the store, not React state: follow permissionsLoaded so this redraws when rights arrive.
  useUserStore((s) => s.permissionsLoaded);
  const canCreate = can('relationships.create');
  const canDelete = can('relationships.delete');
  const canResolve = can('items.edit');
  const [resolveGroupId, setResolveGroupId] = useState<string | null>(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(focusGroupId ?? null);

  const currentWorkspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  const itemsLoading = useItemsStore((s) => s.syncState.isLoading);
  const itemsLastSync = useItemsStore((s) => s.syncState.lastSyncAt);
  const fetchRelationships = useItemRelationshipsStore((s) => s.fetchFromSupabase);
  const relationshipsLoading = useItemRelationshipsStore((s) => s.syncState.isLoading);
  const relationshipsLastSync = useItemRelationshipsStore((s) => s.syncState.lastSyncAt);
  const dissolveGroup = useItemRelationshipsStore((s) => s.dissolveGroup);
  const removeGroupMember = useItemRelationshipsStore((s) => s.removeGroupMember);
  const dissolveEdge = useItemRelationshipsStore((s) => s.dissolveEdge);

  useEffect(() => {
    if (!currentWorkspaceId) return;
    if (relationshipType !== 'lineage') fetchItems();
    void Promise.resolve().then(() => fetchRelationships());
  }, [fetchItems, fetchRelationships, relationshipType, currentWorkspaceId]);

  useEffect(() => {
    if (focusGroupId) setSelectedId(focusGroupId);
  }, [focusGroupId]);

  const groupedData = useGroupedData(relationshipType);
  const edgeData = useEdgeData(relationshipType);
  const reframeGroups = useReframeGroups();
  const { entries: lineageLog, loading: lineageLoading } = useLineageLog();

  const pageAgents = useMemo(
    () =>
      relationshipType === 'dependency' || relationshipType === 'sequence'
        ? [inversionAgent(navigate)]
        : [],
    [relationshipType, navigate],
  );

  const rows: RelationshipListRow[] = useMemo(() => {
    if (relationshipType === 'cluster' || relationshipType === 'package' || relationshipType === 'exclusive') {
      return groupedData.map(({ group, items }) => {
        const title =
          group.title ||
          (relationshipType === 'cluster'
            ? 'Untitled Cluster'
            : relationshipType === 'exclusive'
              ? 'Untitled Competing Set'
              : 'Untitled Package');
        const memberTitles = items.map((i) => i.title).join(' ');
        return {
          id: group.id,
          kind: 'group' as const,
          searchText: `${title} ${group.rationale ?? ''} ${memberTitles}`,
          primaryLabel: title,
          secondaryLabel: `${items.length} item${items.length !== 1 ? 's' : ''}`,
          directionLabel: 'Members',
          roleLabel: group.subtype ? String(group.subtype).replace(/_/g, ' ') : '—',
          familyLabel: meta.title.replace(/s$/, ''),
          statusLabel: group.status,
          rationale: group.rationale ?? '',
          href: `/prioritization/relationships/${
            relationshipType === 'cluster' ? 'clusters' : relationshipType === 'exclusive' ? 'exclusives' : 'packages'
          }/${group.id}`,
          group,
          groupItems: items,
        };
      });
    }

    if (
      relationshipType === 'dependency' ||
      relationshipType === 'prerequisite' ||
      relationshipType === 'sequence' ||
      relationshipType === 'aggregation' ||
      relationshipType === 'elaboration' ||
      relationshipType === 'breakdown' ||
      relationshipType === 'composition' ||
      relationshipType === 'contains'
    ) {
      return edgeData.map(({ edge, sourceItem, targetItem }) => ({
        id: edge.id,
        kind: 'edge' as const,
        searchText: `${sourceItem?.title ?? ''} ${targetItem?.title ?? ''} ${edge.rationale ?? ''} ${edge.type}`,
        primaryLabel: sourceItem?.title ?? 'Unknown',
        secondaryLabel: targetItem?.title ?? 'Unknown',
        directionLabel: directionForPage(relationshipType, edge),
        roleLabel: roleForPage(relationshipType, edge),
        familyLabel: getRelationshipGuideEntry(edge.type)?.label ?? edge.type,
        statusLabel: edge.status,
        rationale: edge.rationale ?? '',
        href: sourceItem ? `/prioritization/items/${sourceItem.id}` : undefined,
        edge,
        sourceItem,
        targetItem,
      }));
    }

    if (relationshipType === 'reframe') {
      return reframeGroups.map((rg) => {
        const primaryTitle = rg.canonicalFrame?.title ?? rg.item.title;
        const frameTypes = rg.secondaryFrames.map((f) => f.frameType).join(' ');
        return {
          id: rg.item.id,
          kind: 'reframe' as const,
          searchText: `${primaryTitle} ${frameTypes} ${rg.item.description ?? ''}`,
          primaryLabel: primaryTitle || 'Untitled',
          secondaryLabel: `${rg.secondaryFrames.length} additional ${rg.secondaryFrames.length !== 1 ? 'categories' : 'category'}`,
          directionLabel: 'Lead → Alternates',
          roleLabel: rg.canonicalFrame?.frameType ?? rg.item.itemCategory ?? '—',
          familyLabel: 'Additional Category',
          statusLabel: 'active',
          rationale: '',
          href: `/prioritization/items/${rg.item.id}`,
          reframe: rg,
        };
      });
    }

    return lineageLog.map((entry) => {
      const cfg = getLineageEventConfig(entry.event.eventType);
      const sourceTitles = entry.sourceItems.map((i) => i?.title ?? '').join(' ');
      const targetTitles = entry.targetItems.map((i) => i?.title ?? '').join(' ');
      return {
        id: entry.event.id,
        kind: 'lineage' as const,
        searchText: `${cfg.label} ${sourceTitles} ${targetTitles} ${entry.event.rationale ?? ''}`,
        primaryLabel: sourceTitles || cfg.label,
        secondaryLabel: targetTitles || '—',
        directionLabel: cfg.verb,
        roleLabel: cfg.label,
        familyLabel: 'Lineage',
        statusLabel: entry.event.createdAt.toLocaleDateString(),
        rationale: entry.event.rationale ?? '',
        lineage: entry,
        lineageEventConfig: cfg,
      };
    });
  }, [relationshipType, groupedData, edgeData, reframeGroups, lineageLog, meta.title]);

  // Items back every row (From/To names, group members, reframe titles) except lineage,
  // which fetches its own items opportunistically — gate on both stores or rows briefly
  // render with the raw item id fallback while items are still in flight behind edges.
  const isStoreLoading =
    relationshipsLoading ||
    (relationshipsLastSync === null && !!currentWorkspaceId) ||
    (relationshipType !== 'lineage' &&
      (itemsLoading || (itemsLastSync === null && !!currentWorkspaceId))) ||
    (relationshipType === 'lineage' && lineageLoading);

  const sortOptions: SortOption<string>[] = useMemo(
    () => [
      { field: 'primaryLabel', label: 'From / Name' },
      { field: 'secondaryLabel', label: 'To / Detail' },
      { field: 'directionLabel', label: 'Direction' },
      { field: 'roleLabel', label: 'Role' },
      { field: 'familyLabel', label: 'Family' },
    ],
    [],
  );

  const tableColumns: TableColumnWithRender<RelationshipListRow>[] = useMemo(
    () => [
      {
        key: 'primaryLabel',
        label:
          relationshipType === 'cluster' || relationshipType === 'package' || relationshipType === 'exclusive'
            ? 'Name'
            : relationshipType === 'reframe'
              ? 'Item'
              : relationshipType === 'lineage'
                ? 'Source'
                : 'From',
        sortable: true,
        minWidth: 220,
        render: (row) => {
          if (row.kind === 'edge') {
            return <ItemTitleLink item={row.sourceItem} fallbackId={row.edge?.sourceItemId} />;
          }
          if (row.kind === 'group') {
            return <span className="text-sm font-medium text-[var(--text-pri)] truncate block">{row.primaryLabel}</span>;
          }
          if (row.kind === 'reframe') {
            return <ItemTitleLink item={row.reframe?.item} />;
          }
          return <span className="text-sm text-[var(--text-pri)] truncate block">{row.primaryLabel}</span>;
        },
      },
      {
        key: 'directionLabel',
        label: 'Direction',
        sortable: true,
        minWidth: 140,
        render: (row) => (
          <span className="inline-flex items-center gap-1 text-xs text-[var(--text-ter)]">
            <ArrowRight className="h-3 w-3 text-[var(--text-ter)] shrink-0" />
            {row.directionLabel}
          </span>
        ),
      },
      {
        key: 'secondaryLabel',
        label:
          relationshipType === 'cluster' || relationshipType === 'package' || relationshipType === 'exclusive'
            ? 'Members'
            : relationshipType === 'reframe'
              ? 'Categories'
              : relationshipType === 'lineage'
                ? 'Target'
                : 'To',
        sortable: true,
        minWidth: 220,
        render: (row) => {
          if (row.kind === 'edge') {
            return <ItemTitleLink item={row.targetItem} fallbackId={row.edge?.targetItemId} />;
          }
          if (row.kind === 'group') {
            return <span className="text-sm text-[var(--text-sec)]">{row.secondaryLabel}</span>;
          }
          if (row.kind === 'reframe' && row.reframe) {
            return (
              <div className="flex flex-wrap gap-1">
                {row.reframe.secondaryFrames.slice(0, 3).map((f) => (
                  <FrameTypeTag key={f.id} type={f.frameType} />
                ))}
                {row.reframe.secondaryFrames.length > 3 && (
                  <span className="text-xs text-[var(--text-ter)]">+{row.reframe.secondaryFrames.length - 3}</span>
                )}
              </div>
            );
          }
          return <span className="text-sm text-[var(--text-sec)] truncate block">{row.secondaryLabel}</span>;
        },
      },
      {
        key: 'roleLabel',
        label: 'Role / authorship',
        sortable: true,
        minWidth: 160,
        render: (row) => (
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border capitalize ${relationshipPillClass(guideId)}`}>
            {row.roleLabel}
          </span>
        ),
      },
      {
        key: 'familyLabel',
        label: 'Family',
        sortable: true,
        minWidth: 120,
        render: (row) => (
          <span className="text-xs text-[var(--text-ter)] capitalize">{row.familyLabel}</span>
        ),
      },
      {
        key: 'statusLabel',
        label: 'Status',
        sortable: true,
        minWidth: 100,
        render: (row) => (
          <span className="text-xs text-[var(--text-ter)] capitalize">{row.statusLabel}</span>
        ),
      },
    ],
    [relationshipType, guideId],
  );

  const renderDetail = useCallback(
    (row: RelationshipListRow) => {
      if (row.kind === 'group' && row.group) {
        return (
          <div className="p-4 space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-[var(--text-pri)]">{row.primaryLabel}</h3>
              <p className="text-xs text-[var(--text-ter)] mt-1">{row.secondaryLabel}</p>
              {row.rationale && <p className="text-sm text-[var(--text-sec)] mt-2">{row.rationale}</p>}
            </div>
            <div className="divide-y divide-[var(--rule)] border border-[var(--rule)] overflow-hidden">
              {(row.groupItems ?? []).map((item) => (
                <div key={item.id} className="flex items-center gap-2 px-3 py-2 hover:bg-[var(--surface)]">
                  <Link to={`/prioritization/items/${item.id}`} className="flex-1 min-w-0 text-sm text-[var(--text-pri)] truncate hover:text-[var(--text-ter)]">
                    {item.title}
                  </Link>
                  {canDelete && (
                    <button
                      type="button"
                      onClick={() => void removeGroupMember(row.group!.id, item.id)}
                      className="p-1 text-[var(--text-ter)] hover:text-[var(--coral)]"
                      title="Remove member"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {row.group.type === 'cluster' && (
              isUnresolvedCluster(row.group) ? (
                <div className="flex items-center justify-between gap-3 border border-[var(--mustard)] bg-[var(--mustard-dim)] px-3 py-2">
                  <p className="text-xs text-[var(--text-pri)]">
                    Not yet resolved. Until you decide duplicate, variant or same item under another category, every member still enters pools and rankings as its own item.
                  </p>
                  {canResolve && (
                    <button
                      type="button"
                      onClick={() => setResolveGroupId(row.group!.id)}
                      className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-[var(--slate)] hover:bg-[var(--slate)] transition-colors"
                    >
                      Resolve cluster…
                    </button>
                  )}
                </div>
              ) : row.group.status === 'confirmed' ? (
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs text-[var(--text-ter)]">
                    Resolved as <span className="font-medium text-[var(--text-sec)]">{row.group.subtype ?? 'cluster'}</span>
                    {row.group.reviewedAt ? ` on ${new Date(row.group.reviewedAt).toLocaleDateString()}` : ''}.
                  </p>
                  {/* Unmerge (mig 746): the resolver or an admin, while the
                      closed items are untouched since; the server says why not. */}
                  {canResolve && (
                    <ReverseActionButton
                      label="Reopen cluster"
                      confirmTitle="Reopen this cluster"
                      explain="The items this resolution closed come back as separate, active items and the cluster returns to unresolved. It can't be reopened once a closed item has been used since."
                      run={(reason) => reopenResolvedCluster(row.group!.id, reason)}
                      onDone={() => {
                        void fetchRelationships();
                        void fetchItems();
                      }}
                    />
                  )}
                </div>
              ) : null
            )}
            {canDelete && (
              <button
                type="button"
                onClick={() => void dissolveGroup(row.group!.id)}
                className="text-xs text-[var(--coral)] hover:underline"
              >
                Dissolve group
              </button>
            )}
          </div>
        );
      }

      if (row.kind === 'edge' && row.edge) {
        return (
          <div className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <ItemTitleLink item={row.sourceItem} fallbackId={row.edge.sourceItemId} />
              <ArrowRight className="h-4 w-4 text-[var(--text-ter)] shrink-0" />
              <ItemTitleLink item={row.targetItem} fallbackId={row.edge.targetItemId} />
            </div>
            <p className="text-xs text-[var(--text-ter)]">{row.directionLabel}</p>
            <p className="text-xs text-[var(--text-ter)]">Authorship: {row.roleLabel}</p>
            {row.rationale && <p className="text-sm text-[var(--text-sec)]">{row.rationale}</p>}
            {edgeMaterializesIntoPlanning(row.edge) && (
              <p className="text-xs text-[var(--mustard)] dark:text-[var(--mustard)]">
                This relationship is also a Planning dependency. Dissolving it here removes it from
                Planning on the next sync.
              </p>
            )}
            {canDelete && (
              <button
                type="button"
                onClick={() => {
                  // Prioritization owns the edge; Planning only materializes it. Dissolving
                  // used to silently delete a plan's dependency with no signal on either
                  // surface — say so before it happens, not after.
                  const alsoInPlanning = edgeMaterializesIntoPlanning(row.edge!);
                  const message = alsoInPlanning
                    ? 'Dissolve this relationship?\n\nIt is also a dependency in Planning — that dependency will be removed on the next sync.'
                    : 'Dissolve this relationship?';
                  if (!confirm(message)) return;
                  void dissolveEdge(row.edge!.id);
                }}
                className="text-xs text-[var(--coral)] hover:underline"
              >
                Dissolve relationship
              </button>
            )}
          </div>
        );
      }

      if (row.kind === 'reframe' && row.reframe) {
        return (
          <div className="p-4 space-y-3">
            <ItemTitleLink item={row.reframe.item} />
            <div className="space-y-2">
              {row.reframe.canonicalFrame && (
                <div className="border border-[var(--rule)] p-3">
                  <FrameTypeTag type={(row.reframe.canonicalFrame.frameType ?? 'problem') as ItemLanguageType} />
                  <p className="text-xs text-[var(--text-ter)] mt-1">Lead category</p>
                  {row.reframe.canonicalFrame.description && (
                    <p className="text-sm text-[var(--text-sec)] mt-1">{row.reframe.canonicalFrame.description}</p>
                  )}
                </div>
              )}
              {row.reframe.secondaryFrames.map((frame) => (
                <div key={frame.id} className="border border-[var(--rule)] p-3">
                  <FrameTypeTag type={frame.frameType} />
                  <p className="text-sm font-medium text-[var(--text-pri)] mt-1">{frame.title}</p>
                  {frame.description && <p className="text-xs text-[var(--text-ter)] mt-1">{frame.description}</p>}
                </div>
              ))}
            </div>
          </div>
        );
      }

      if (row.kind === 'lineage' && row.lineage) {
        const cfg =
          row.lineageEventConfig ?? getLineageEventConfig(row.lineage.event.eventType);
        const sources = row.lineage.sourceItems.filter(Boolean) as Candidate[];
        const targets = row.lineage.targetItems.filter(Boolean) as Candidate[];
        return (
          <div className="p-4 space-y-4">
            <div className="space-y-1">
              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}>
                {cfg.label}
              </span>
              <p className="text-sm text-[var(--text-sec)]">{cfg.verb}</p>
              {row.rationale && <p className="text-sm text-[var(--text-ter)] italic">"{row.rationale}"</p>}
            </div>
            {sources.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide">Source</p>
                <div className="divide-y divide-[var(--rule)] border border-[var(--rule)] overflow-hidden">
                  {sources.map((item) => (
                    <div key={item.id} className="px-3 py-2">
                      <ItemTitleLink item={item} />
                    </div>
                  ))}
                </div>
              </div>
            )}
            {targets.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide">Target</p>
                <div className="divide-y divide-[var(--rule)] border border-[var(--rule)] overflow-hidden">
                  {targets.map((item) => (
                    <div key={item.id} className="px-3 py-2">
                      <ItemTitleLink item={item} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      }

      return null;
    },
    [canDelete, canResolve, dissolveEdge, dissolveGroup, removeGroupMember, fetchItems, fetchRelationships],
  );

  const hasCreate = [
    'cluster',
    'package',
    'exclusive',
    'dependency',
    'prerequisite',
    'sequence',
    'composition',
    'contains',
    'elaboration',
  ].includes(relationshipType);

  return (
    <div className="h-full min-h-[520px] flex flex-col gap-3">
      {pageAgents.length > 0 && <AgentBar agents={pageAgents} variant="inline" />}

      <EntityListPage<RelationshipListRow, string>
        items={rows}
        loading={isStoreLoading}
        getId={(row) => row.id}
        title={meta.title}
        subtitle={meta.subtitle}
        icon={guideIcon(guideId)}
        iconBgClassName={guideIconBg(guideId)}
        itemLabel={meta.itemLabel}
        searchFields={['searchText', 'primaryLabel', 'secondaryLabel', 'directionLabel', 'roleLabel', 'familyLabel', 'rationale']}
        searchPlaceholder={`Search ${meta.title.toLowerCase()}…`}
        sortOptions={sortOptions}
        defaultSort={{ field: 'primaryLabel', direction: 'asc' }}
        viewModes={['table']}
        defaultViewMode="table"
        selectable={false}
        detailPanel
        selectedId={selectedId}
        onSelectionChange={setSelectedId}
        renderDetail={renderDetail}
        tableColumns={tableColumns}
        onRefresh={() => {
          if (relationshipType !== 'lineage') fetchItems();
          void fetchRelationships();
        }}
        onCreate={hasCreate && canCreate ? () => setShowCreateModal(true) : undefined}
        createLabel={meta.createLabel}
        headerActions={
          <button
            type="button"
            onClick={() => setShowAbout(true)}
            className="flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] px-2 py-1.5 transition-colors"
            title={`About ${meta.title}`}
          >
            <Info className="w-3.5 w-3.5" />
            About
          </button>
        }
        emptyTitle={meta.emptyMessage}
        emptyDescription={meta.definition}
        emptyAction={
          hasCreate && canCreate
            ? { label: meta.createLabel, onClick: () => setShowCreateModal(true) }
            : undefined
        }
      />

      {relationshipType === 'cluster' && (
        <ResolveClusterModal
          open={resolveGroupId !== null}
          groupId={resolveGroupId}
          onClose={() => setResolveGroupId(null)}
          onResolved={({ outcome, canonicalItemId }) => {
            fetchRelationships();
            // Duplicate and reframe both keep one item and close the rest —
            // go to the one that stands. Variant keeps every member, so the
            // resolved cluster (shown here) is where the story continues.
            if ((outcome === 'reframe' || outcome === 'duplicate') && canonicalItemId) {
              navigate(`/prioritization/items/${canonicalItemId}`);
            }
          }}
        />
      )}
      {relationshipType === 'cluster' && (
        <CreateCanonicalClusterModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            fetchRelationships();
          }}
        />
      )}
      {relationshipType === 'package' && (
        <CreatePackageModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            fetchRelationships();
          }}
        />
      )}
      {relationshipType === 'exclusive' && (
        <CreateExclusiveModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            fetchRelationships();
          }}
        />
      )}
      {(relationshipType === 'sequence' || relationshipType === 'dependency' || relationshipType === 'prerequisite') && (
        <AddSequenceLinkModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            fetchRelationships();
          }}
        />
      )}
      {(relationshipType === 'composition' || relationshipType === 'aggregation' || relationshipType === 'breakdown') && (
        <AddCompositionModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            fetchRelationships();
          }}
        />
      )}
      {relationshipType === 'contains' && (
        <AddContainsModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            fetchRelationships();
          }}
        />
      )}
      {relationshipType === 'elaboration' && (
        <AddElaborationModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            fetchRelationships();
          }}
        />
      )}

      {showAbout && (
        <AboutModal
          title={meta.title}
          definition={meta.definition}
          diagram={meta.diagram}
          guideId={guideId}
          onClose={() => setShowAbout(false)}
        />
      )}
    </div>
  );
}
